import { Component, OnInit, OnDestroy, DestroyRef, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { PresalesService } from '../../services/presales.service';
import {
  PresalesClient,
  CreatePresalesClient,
  INDUSTRIES,
  COUNTRIES,
  LEAD_SOURCES
} from '../../models/presales.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';

interface Option { label: string; value: string; }

@Component({
  selector: 'app-presales-client-management',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, Button, Dialog, Select,
    InputText, Checkbox, Tooltip, ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './presales-client-management.component.html',
  styleUrl: './presales-client-management.component.scss'
})
export class PresalesClientManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  clients: PresalesClient[] = [];
  filteredClients: PresalesClient[] = [];
  loading = true;
  searchText = '';

  // Filters
  selectedStatus: 'active' | 'inactive' | null = null;
  selectedIndustry: string | null = null;
  selectedCountry: string | null = null;

  // Dropdown options
  industryOptions: Option[]  = INDUSTRIES.map(i => ({ label: i, value: i }));
  countryOptions: Option[]   = COUNTRIES.map(c => ({ label: c, value: c }));
  leadSourceOptions: Option[] = LEAD_SOURCES.map(s => ({ label: s, value: s }));

  statusFilterOptions: Option[] = [
    { label: 'Active',   value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  // Dialog state
  displayDialog = false;
  isEditMode = false;
  selectedClient: PresalesClient = this.emptyClient();
  fieldTouched: Record<string, boolean> = {};

  // Access — Admin + PM can add / edit (matches PresalesAccess policy)
  canManagePresales = false;

  constructor(
    private presalesService: PresalesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search presales clients…');
    this.pageHeaderService.setPrimaryButtonLabel('Add Presales Client');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);

    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.canManagePresales = !!(user?.isAdmin || user?.isPM);
      this.pageHeaderService.setCanShowPrimaryAction(this.canManagePresales);
    });

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.searchText = v ?? ''; this.applyFilters(); });

    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showAddDialog());

    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ngZone.run(() => this.exportToExcel()));

    this.loadClients();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadClients(): void {
    this.loading = true;
    this.presalesService.getAllClients()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as PresalesClient[])))
      .subscribe(data => {
        this.clients = data || [];
        this.applyFilters();
        this.loading = false;
      });
  }

  applyFilters(): void {
    let list = [...this.clients];
    const s = this.searchText.trim().toLowerCase();
    if (s) {
      list = list.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.contactPerson?.toLowerCase().includes(s) ||
        c.contactEmail?.toLowerCase().includes(s) ||
        c.industry?.toLowerCase().includes(s) ||
        c.country?.toLowerCase().includes(s)
      );
    }
    if (this.selectedStatus === 'active') {
      list = list.filter(c => c.isActive === true);
    } else if (this.selectedStatus === 'inactive') {
      list = list.filter(c => c.isActive === false);
    }
    if (this.selectedIndustry) {
      list = list.filter(c => c.industry === this.selectedIndustry);
    }
    if (this.selectedCountry) {
      list = list.filter(c => c.country === this.selectedCountry);
    }
    this.filteredClients = list;
    this.pageHeaderService.setExportDisabled(this.filteredClients.length === 0);
  }

  clearFilters(): void {
    this.selectedStatus = null;
    this.selectedIndustry = null;
    this.selectedCountry = null;
    this.pageHeaderService.setSearchText('');
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.selectedStatus || this.selectedIndustry || this.selectedCountry);
  }

  exportToExcel(): void {
    if (!this.filteredClients.length) return;
    const rows = this.filteredClients.map(c => ({
      'Name':          c.name,
      'Industry':      c.industry || '',
      'Country':       c.country || '',
      'Contact':       c.contactPerson || '',
      'Email':         c.contactEmail || '',
      'Phone':         c.contactPhone || '',
      'Website':       c.companyWebsite || '',
      'Lead Source':   c.leadSource || '',
      'Notes':         c.notes || '',
      'Active':        c.isActive ? 'Yes' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb: XLSX.WorkBook = { Sheets: { 'Presales Clients': ws }, SheetNames: ['Presales Clients'] };
    const buffer: unknown = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer as ArrayBuffer], { type: 'application/octet-stream' });
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    saveAs(blob, `Presales_Clients_${stamp}.xlsx`);
  }

  // ================= Stats =================

  get totalCount(): number { return this.clients.length; }
  get topLeadSource(): string {
    if (this.clients.length === 0) return '—';
    const counts = new Map<string, number>();
    for (const c of this.clients) {
      if (!c.leadSource) continue;
      counts.set(c.leadSource, (counts.get(c.leadSource) ?? 0) + 1);
    }
    if (counts.size === 0) return '—';
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }

  // ================= Dialog / CRUD =================

  showAddDialog(): void {
    this.selectedClient = this.emptyClient();
    this.isEditMode = false;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  showEditDialog(client: PresalesClient): void {
    this.selectedClient = { ...client };
    this.isEditMode = true;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  hideDialog(): void {
    this.displayDialog = false;
  }

  saveClient(): void {
    this.fieldTouched['name'] = true;
    if (!this.selectedClient.name?.trim()) return;

    const dto: CreatePresalesClient = {
      name: this.selectedClient.name.trim(),
      description: this.selectedClient.description || undefined,
      contactPerson: this.selectedClient.contactPerson || undefined,
      contactEmail: this.selectedClient.contactEmail || undefined,
      contactPhone: this.selectedClient.contactPhone || undefined,
      industry: this.selectedClient.industry || undefined,
      country: this.selectedClient.country || undefined,
      companyWebsite: this.selectedClient.companyWebsite || undefined,
      leadSource: this.selectedClient.leadSource || undefined,
      notes: this.selectedClient.notes || undefined,
      isActive: this.selectedClient.isActive
    };

    if (this.isEditMode && this.selectedClient.id) {
      this.presalesService.updateClient(this.selectedClient.id, dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (saved) => {
            const idx = this.clients.findIndex(c => c.id === saved.id);
            if (idx >= 0) this.clients[idx] = saved;
            this.applyFilters();
            this.messageService.add({ severity: 'success', summary: 'Updated', detail: `"${saved.name}" saved` });
            this.hideDialog();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update client' })
        });
    } else {
      this.presalesService.addClient(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.clients = [created, ...this.clients];
            this.applyFilters();
            this.messageService.add({ severity: 'success', summary: 'Created', detail: `"${created.name}" added` });
            this.hideDialog();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add client' })
        });
    }
  }

  /**
   * Flip the client's active state via the standard update endpoint — same
   * pattern as client-management.toggleActiveStatus.
   */
  toggleActive(client: PresalesClient): void {
    const activating = !client.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${client.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
    const dto: CreatePresalesClient = {
      name:           client.name,
      description:    client.description || undefined,
      contactPerson:  client.contactPerson || undefined,
      contactEmail:   client.contactEmail || undefined,
      contactPhone:   client.contactPhone || undefined,
      industry:       client.industry || undefined,
      country:        client.country || undefined,
      companyWebsite: client.companyWebsite || undefined,
      leadSource:     client.leadSource || undefined,
      notes:          client.notes || undefined,
      isActive:       !client.isActive,
    };

    this.presalesService.updateClient(client.id, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          const idx = this.clients.findIndex(c => c.id === saved.id);
          if (idx >= 0) this.clients[idx] = saved;
          this.applyFilters();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Presales client ${saved.isActive ? 'activated' : 'deactivated'} successfully`
          });
        },
        error: () => this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update client status'
        })
      });
      }
    });
  }

  // ================= Helpers =================

  private emptyClient(): PresalesClient {
    return {
      id: '',
      name: '',
      description: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      industry: '',
      country: '',
      companyWebsite: '',
      leadSource: '',
      notes: '',
      isActive: true,
      promotedToClientId: null,
      promotedToClientName: null
    };
  }
}
